import {FC, ReactNode} from 'react'
import {useSelector} from 'react-redux'
import {
  getAnonymousId,
  getCurrencyCode,
  getDeviceCategory,
  getDevicePlatform,
  getLocaleCode,
  getUserCountryCode
} from 'modules/meta/selectors'
import {
  getSrpSearchFlow,
  getTrafficSource,
  hasDates,
  hasDefaultDates
} from 'utils/trafficSource'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {getUrlParams} from '@vio/core/src/_web/utils/url'
import {getUserDeviceDetails} from '@vio/core/src/_web/utils/userAgent'
import {useAddFeatureFlagTracking} from '@vio/core/src/analytics/hooks/useAddFeatureFlagTracking'
import {
  selectHasVioEmail,
  selectIsAuthenticated,
  selectIsEmployee,
  selectIsMemberPlus
} from '@vio/core/src/auth/modules/selectors'
import {useOpticks} from '@vio/core/src/experiments/components/OpticksProvider'
import {useOpticksAudienceSegmentation} from '@vio/core/src/experiments/hooks/useOpticksAudienceSegmentation'
import {getForcedTogglesFromSearchQuery} from '@vio/core/src/experiments/utils/getForcedTogglesFromSearchQuery'
import {getMobileAppVersion} from '@vio/core/src/native'
import {isMetaProfile} from '@vio/core/src/profile'
import {parseQueryString} from '@vio/core/src/utils/url'
import {useDebugPanelToggles} from '@vio/debug-panel'

import type {AudienceSegmentationAttributes} from '@vio/core/src/experiments/types'

interface Props {
  forcedToggles?: Record<string, string>
  children: ReactNode
}

const useSegmentationAttributes = (
  queryString: string
): AudienceSegmentationAttributes => {
  const searchParameters = parseQueryString(queryString, true)
  const {brandCode, brand} = useBrand()
  const {utm_source, profile, srpSearchFlow, label} = searchParameters
  const trafficSource = getTrafficSource(utm_source as string, label as string)

  return {
    application: 'search',
    env: process.env.NEXT_PUBLIC_ENV || 'prd',
    brandCode,
    deviceCategory: useSelector(getDeviceCategory),
    devicePlatform: useSelector(getDevicePlatform),
    hasLandingDates: hasDates(searchParameters),
    hasPrefilledDates: hasDefaultDates(searchParameters),
    hasVioEmail: useSelector(selectHasVioEmail),
    isEmployee: useSelector(selectIsEmployee),
    isMetaTrafficProfile: isMetaProfile(profile as string),
    locale: useSelector(getLocaleCode),
    trafficSource,
    userCountry: useSelector(getUserCountryCode),
    srpSearchFlow: getSrpSearchFlow(
      brand,
      trafficSource,
      srpSearchFlow as string
    )
  }
}

const urlParams = getUrlParams()

const InitializeABTesting: FC<Props> = ({children, forcedToggles}) => {
  const Opticks = useOpticks()
  const anonymousId = useSelector(getAnonymousId)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isMemberPlus = useSelector(selectIsMemberPlus)
  const currency = useSelector(getCurrencyCode)

  const queryString: string =
    (typeof window !== 'undefined' && window?.location?.search) || ''

  const {applyLocalstorageToggles} = useDebugPanelToggles({
    params: queryString
  })

  const opticksAttributes = useSegmentationAttributes(queryString)

  const audienceSegmentationAttributes = (): AudienceSegmentationAttributes => {
    // Some of the attributes below use client-side APIs, so we bail out until we figure out
    // how to handle this on the server.
    if (typeof window === 'undefined') {
      return {} as AudienceSegmentationAttributes
    }

    const {userAgentDetails} = getUserDeviceDetails()

    // Important: When adding a new segmentation attribute, double check if it can also be added in the backend.
    // @file packages/client/src/utils/server/getExperimentSegmentationAttributes.ts
    // This will be tacked in this story sc-168717
    return {
      ...opticksAttributes,
      isAuthenticated,
      isMemberPlus,
      currency,
      mobileAppVersion: getMobileAppVersion(),
      profile: typeof urlParams.profile === 'string' ? urlParams.profile : '',
      pathName: typeof window !== 'undefined' ? location.pathname : '',
      isBot: Boolean(userAgentDetails.isBot),
      userAgent: userAgentDetails.source
    }
  }

  useOpticksAudienceSegmentation(audienceSegmentationAttributes())

  useAddFeatureFlagTracking()

  // We do not support Opticks on the server, no need to initialize it
  if (typeof window !== 'undefined' && anonymousId && Opticks) {
    Opticks.setUserId(anonymousId)
    Opticks.setAudienceSegmentationAttributes(audienceSegmentationAttributes())

    const forcedTogglesFromQuery = getForcedTogglesFromSearchQuery(queryString)

    const allForcedToggles = applyLocalstorageToggles({
      ...forcedToggles,
      ...forcedTogglesFromQuery
    })

    if (allForcedToggles) {
      Opticks.forceToggles(allForcedToggles)
    }

    // Test to make sure our AB testing infrastructure is working as intended
    Opticks.toggle('a-a-experiment')
    Opticks.toggle('server-a-a')
  }

  return <>{children}</>
}

export default InitializeABTesting
